import { Box, HStack, Spinner, Stack } from "@chakra-ui/react"
import { ActionButton, BackButton, CancelButton } from "@hydrologiq/shared/content/components/Button"
import {
  ButtonContent,
  ActionButtonTypes,
  useActionButtonContent,
} from "@hydrologiq/shared/content/hooks/useButtonContent"
import { ReactNode } from "react"

export type FormActionOverrideProps = {
  onCancel: () => void
  onSubmit: () => Promise<boolean> | boolean
  onSecondaryAction?: () => boolean
  canSubmit: boolean
}

interface FormContainerProps {
  onSubmit?: () => Promise<boolean> | boolean
  onCancel?: () => void
  onBack?: () => void
  onSecondaryAction?: () => boolean
  heading?: string | ((props: FormActionOverrideProps) => ReactNode)
  helperText?: string | ReactNode
  children?: ReactNode
  loading?: boolean
  canSubmit?: boolean
  actionType?: ActionButtonTypes
  actionVariant?: "primary" | "secondary"
  actionContent?: ButtonContent
  secondaryActionType?: ActionButtonTypes
  secondaryActionContent?: ButtonContent
  layout?: "row" | "column"
  ActionOverride?: (props: FormActionOverrideProps) => ReactNode
  outsideStack?: ReactNode
  useFormTag?: boolean // If false, uses div instead of form tag to avoid nested forms
}

export const ActionContainer = ({
  children,
  showBackButton = false,
}: {
  children: ReactNode
  showBackButton?: boolean
}) => {
  return (
    <Stack
      zIndex={3}
      position="sticky"
      bottom="-16px"
      width="100%"
      pl={"25px"}
      pr={"25px"}
      backgroundColor="background"
      direction="row"
      justifyContent={showBackButton ? "space-between" : "flex-end"}
      py={5}
      flexShrink={0}
      borderTopWidth="1px"
      borderTopColor="gray.100"
    >
      {children}
    </Stack>
  )
}

function FormContainer(props: FormContainerProps) {
  const {
    onSubmit,
    onCancel,
    onBack,
    onSecondaryAction,
    heading,
    helperText,
    children,
    loading,
    canSubmit,
    actionType,
    actionVariant,
    actionContent: defaultActionContent,
    secondaryActionType,
    secondaryActionContent: defaultSecondaryActionContent,
    layout,
    ActionOverride,
    outsideStack,
    useFormTag,
  } = props

  let actionContent = useActionButtonContent(actionType)
  if (actionVariant && actionContent) actionContent.variant = actionVariant
  if (actionContent && defaultActionContent) actionContent = { ...actionContent, ...defaultActionContent }

  let secondaryActionContent = useActionButtonContent(secondaryActionType)
  if (secondaryActionContent && defaultSecondaryActionContent)
    secondaryActionContent = { ...secondaryActionContent, ...defaultSecondaryActionContent }
  const _canSubmit = loading === undefined ? canSubmit : !loading && canSubmit
  const showActions = onCancel || onSecondaryAction || actionContent || ActionOverride
  const actions = showActions && (
    <ActionContainer showBackButton={!!onBack}>
      {ActionOverride !== undefined ? (
        <ActionOverride
          onCancel={onCancel || (() => {})}
          onSubmit={
            onSubmit ||
            (() => {
              return true
            })
          }
          onSecondaryAction={onSecondaryAction}
          canSubmit={_canSubmit ?? false}
        />
      ) : (
        <HStack width="100%" justifyContent={onBack ? "space-between" : "flex-end"}>
          {/* Back button on the left */}
          {onBack && (
            <BackButton
              disabled={loading}
              onClick={(e) => {
                e.preventDefault()
                onBack()
              }}
            />
          )}

          {/* Action buttons on the right */}
          <HStack>
            {/* Only show Cancel button if there's no Back button */}
            {!onBack && onCancel && (
              <CancelButton
                disabled={loading}
                onClick={(e) => {
                  e.preventDefault()
                  onCancel()
                }}
              />
            )}
            {secondaryActionContent && onSecondaryAction && (
              <ActionButton
                context={secondaryActionContent}
                value="secondary"
                disabled={loading}
                onClick={(e) => {
                  e.preventDefault()
                  if (onSecondaryAction()) {
                    // formRef.current?.reset()
                  }
                }}
              />
            )}
            {actionContent && (
              <ActionButton
                context={actionContent}
                onClick={async (e) => {
                  e.preventDefault()
                  if (onSubmit) {
                    await onSubmit()
                  }
                }}
                value="primary"
                disabled={!_canSubmit}
              />
            )}
          </HStack>
        </HStack>
      )}
    </ActionContainer>
  )
  const tag = useFormTag ? "form" : "div"
  return (
    <Box width="100%" position="relative">
      <Box width="100%" as={tag}>
        {children}
        {actions}
      </Box>

      {/* Enhanced loading overlay */}
      {loading && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="rgba(255, 255, 255, 0.8)"
          borderRadius="md"
          zIndex={1}
        >
          <Stack alignItems="center" gap={3}>
            <Spinner size="lg" color="blue.500" />
            <Box fontSize="sm" color="gray.600" fontWeight="medium">
              Processing...
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default FormContainer
